// Complete local-first brain for cartographer.
// The entire experience (mapping, legend, tile suggestions, gallery simulation) works
// offline with `npm run dev`. When the Worker is present the same shapes are used against D1 + AI + R2.
// Ownership uses the same high-entropy secret pattern: whoever holds the key controls the maps.

import type {
  MapRecord, Signals, VisualState, WisdomTile, Motif,
  CareBurden, LoadNote, HandoffState, TileSuggestion, LocalAI
} from './types'

const LS_MAPS = 'cartographer.maps.v2'
const LS_TILES = 'cartographer.tiles.v2' // local private + simulated public
const LS_OWNER = 'cartographer.ownerKey.v2'
const LS_DRAFT = 'cartographer.currentDraft.v2'

function loadMaps(): Record<string, MapRecord> {
  try { return JSON.parse(localStorage.getItem(LS_MAPS) || '{}') } catch { return {} }
}
function saveMaps(m: Record<string, MapRecord>) { localStorage.setItem(LS_MAPS, JSON.stringify(m)) }

function loadTiles(): Record<string, WisdomTile> {
  try { return JSON.parse(localStorage.getItem(LS_TILES) || '{}') } catch { return {} }
}
function saveTiles(t: Record<string, WisdomTile>) { localStorage.setItem(LS_TILES, JSON.stringify(t)) }

export function getOwnerKey(): string {
  let key = localStorage.getItem(LS_OWNER)
  if (!key) {
    key = 'ca_' + Array.from(crypto.getRandomValues(new Uint8Array(18)))
      .map(b => b.toString(36)).join('').slice(0, 28)
    localStorage.setItem(LS_OWNER, key)
  }
  return key
}

export function hashOwner(key: string): string {
  let h = 2166136261 >>> 0
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return 'o_' + h.toString(36)
}

function nowIso() { return new Date().toISOString() }

export function getCurrentDraft(): Partial<MapRecord> | null {
  try { return JSON.parse(localStorage.getItem(LS_DRAFT) || 'null') } catch { return null }
}
export function saveCurrentDraft(draft: Partial<MapRecord>) {
  localStorage.setItem(LS_DRAFT, JSON.stringify(draft))
}
export function clearCurrentDraft() { localStorage.removeItem(LS_DRAFT) }

export function listMyMaps(ownerKey?: string): MapRecord[] {
  const key = ownerKey || getOwnerKey()
  const h = hashOwner(key)
  const all = loadMaps()
  return Object.values(all)
    .filter(m => m.ownerHash === h && !m.deleted_at)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export function getMap(id: string, ownerKey?: string): MapRecord | null {
  const all = loadMaps()
  const m = all[id]
  if (!m) return null
  const h = hashOwner(ownerKey || getOwnerKey())
  if (m.ownerHash !== h) return null
  return m
}

export function createMap(anchor: string): MapRecord {
  const ownerKey = getOwnerKey()
  const ownerHash = hashOwner(ownerKey)
  const id = 'map_' + crypto.randomUUID().slice(0, 12)
  const now = nowIso()

  const initialSignals: Signals = {
    care_burdens: [],
    anticipation: { level: 2 },
    memory: { level: 2 },
    handoff: { level: 2, frictions: [] },
    capacity: { level: 2 }
  }

  const initialVisual: VisualState = {
    care: 0.4,
    anticipation: 0.6,
    memory: 0.55,
    handoff: 0.65,
    capacity: 0.45
  }

  const m: MapRecord = {
    id,
    ownerHash,
    createdAt: now,
    updatedAt: now,
    anchor: anchor.trim(),
    signals: initialSignals,
    visualState: initialVisual,
    status: 'active'
  }

  const all = loadMaps()
  all[id] = m
  saveMaps(all)
  return m
}

export function updateMap(id: string, patch: Partial<MapRecord> | ((m: MapRecord) => MapRecord)): MapRecord | null {
  const all = loadMaps()
  const existing = all[id]
  if (!existing) return null
  const next = typeof patch === 'function' ? patch({ ...existing }) : { ...existing, ...patch }
  next.updatedAt = nowIso()
  all[id] = next
  saveMaps(all)
  return next
}

export function deleteMap(id: string): void {
  const all = loadMaps()
  if (all[id]) {
    all[id] = { ...all[id], deleted_at: nowIso(), status: 'archived' }
    saveMaps(all)
  }
}

export function updateSignals(id: string, signals: Signals, visual?: VisualState): MapRecord | null {
  return updateMap(id, m => ({
    ...m,
    signals,
    visualState: visual || deriveVisualFromSignals(signals, m.visualState)
  }))
}

export function setAnchor(id: string, anchor: string): MapRecord | null {
  return updateMap(id, m => ({ ...m, anchor: anchor.trim() }))
}

export function setPrivateNotes(id: string, notes: string): MapRecord | null {
  return updateMap(id, m => ({ ...m, privateNotes: notes }))
}

export function setPrivateLegend(id: string, legend: string): MapRecord | null {
  return updateMap(id, m => ({ ...m, privateLegend: legend }))
}

export function setVisualState(id: string, vs: VisualState): MapRecord | null {
  return updateMap(id, m => ({ ...m, visualState: vs }))
}

// Derive a reasonable visual pentagon from the structured signals (used for local + as starting point)
export function deriveVisualFromSignals(s: Signals, prev?: VisualState): VisualState {
  const care = Math.min(1, Math.max(0.15, (s.care_burdens.length / 4) + (s.care_burdens.includes('repair') ? 0.15 : 0)))
  const ant = s.anticipation.level / 3
  const mem = s.memory.level / 3
  const hand = s.handoff.level / 3
  const cap = s.capacity.level / 3

  // Blend with previous to avoid jarring jumps when user drags vs edits
  if (prev) {
    return {
      care: lerp(prev.care, care, 0.65),
      anticipation: lerp(prev.anticipation, ant, 0.65),
      memory: lerp(prev.memory, mem, 0.65),
      handoff: lerp(prev.handoff, hand, 0.65),
      capacity: lerp(prev.capacity, cap, 0.65)
    }
  }
  return { care, anticipation: ant, memory: mem, handoff: hand, capacity: cap }
}

function lerp(a: number, b: number, t: number) { return a + (b - a) * t }

// ---------- Handoff friction palette (the things that make the pass sticky) ----------
export const HANDOFF_FRICTIONS = [
  'no clean close',
  'tone risk (will be misread)',
  'asymmetry of investment',
  'they may need more than I have',
  'the silence is already speaking',
  'fear the reply chain will explode',
  'I owe more context than I can give',
  'I do not know what I feel yet',
  'protecting the relationship from my exhaustion'
] as const

// ---------- Local AI (deterministic, wise, never embarrassing) ----------
export const localAI: LocalAI = {
  generateLegend(signals: Signals, anchor: string, notes?: string): string {
    const burdens = signals.care_burdens.length ? signals.care_burdens.join(', ') : 'unnamed care'
    const ant = ['light', 'gathering', 'heavy'][signals.anticipation.level - 1]
    const mem = ['thin', 'layered', 'thick'][signals.memory.level - 1]
    const hand = ['open', 'sticky', 'knotted'][signals.handoff.level - 1]
    const cap = ['full', 'modest', 'thin'][signals.capacity.level - 1]

    const lines: string[] = []

    lines.push(`This wait is carrying ${burdens}. The anticipation feels ${ant}. The history is ${mem}. The handoff is ${hand}. Your capacity right now is ${cap}.`)

    if (signals.anticipation.level >= 2) {
      lines.push('The mind is already running the conversation that has not happened yet. That rehearsal is real work, even when it produces no message.')
    }
    if (signals.memory.level >= 2) {
      lines.push('There is an archive in the body. Previous versions of this thread, previous silences, previous selves are all present when you consider the cursor.')
    }
    if (signals.handoff.level >= 2 && signals.handoff.frictions.length) {
      lines.push(`What makes the pass difficult: ${signals.handoff.frictions.slice(0, 3).join(' • ')}. These are not character flaws. They are conditions.`)
    }
    if (signals.capacity.level <= 2) {
      lines.push('You are being asked (by yourself or the situation) to reply from a smaller reserve than the situation seems to deserve. That mismatch is the map.')
    }

    if (notes && notes.trim().length > 20) {
      lines.push('You also noted: ' + notes.trim().slice(0, 160) + (notes.length > 160 ? '…' : ''))
    }

    lines.push('There is no correct shape for this wait. The map does not tell you what to do. It makes the weight legible so it stops governing you in secret.')

    return lines.join('\n\n')
  },

  suggestTiles(signals: Signals, legend?: string): TileSuggestion[] {
    const suggestions: TileSuggestion[] = []
    const hasRepair = signals.care_burdens.includes('repair')
    const highAnt = signals.anticipation.level >= 2
    const thickMem = signals.memory.level >= 2
    const knotted = signals.handoff.level >= 2
    const thinCap = signals.capacity.level <= 2

    if (highAnt && thinCap) {
      suggestions.push({
        text: 'Many of us are rehearsing replies we do not yet have the breath to send. The rehearsal itself becomes the debt we feel we owe.',
        motif: 'fog'
      })
    }
    if (hasRepair && knotted) {
      suggestions.push({
        text: 'The hardest handoff is often the one that would require us to be both accountable and small at the same time. Silence sometimes protects the possibility of both.',
        motif: 'knot'
      })
    }
    if (thickMem) {
      suggestions.push({
        text: 'A delayed reply is rarely a single message. It is a whole correspondence we are afraid of reopening or unable to carry forward cleanly.',
        motif: 'letters'
      })
    }
    if (thinCap) {
      suggestions.push({
        text: 'Capacity is not a moral failing. Sometimes the honest reply would cost more internal resource than we currently hold. Naming that is itself a form of care.',
        motif: 'horizon'
      })
    }
    if (!highAnt && !knotted) {
      suggestions.push({
        text: 'Not every silence is charged. Some waits are simply the time it takes for the right version of the truth to become speakable without harm.',
        motif: 'door'
      })
    }

    // Always offer at least two grounded options
    if (suggestions.length < 2) {
      suggestions.push({
        text: 'The map of an unsent reply often reveals that the difficulty lives less in the content and more in the conditions under which the content would have to arrive.',
        motif: 'arch'
      })
    }
    if (suggestions.length < 2) {
      suggestions.push({
        text: 'We treat the unsent message as a failure of character when it is frequently a failure of conditions: time, energy, safety, clarity, or the simple fact that two people are carrying different loads of the same thread.',
        motif: 'thread'
      })
    }

    return suggestions.slice(0, 3)
  }
}

// Seed a couple of beautiful public example tiles so the Atlas is never empty on first visit (local only)
export function seedExampleTilesIfEmpty() {
  const tiles = loadTiles()
  if (Object.keys(tiles).length > 0) return

  const examples: WisdomTile[] = [
    {
      id: 'tile_ex1',
      mapId: 'map_ex1',
      createdAt: new Date(Date.now() - 1000 * 3600 * 24 * 9).toISOString(),
      publishedAt: new Date(Date.now() - 1000 * 3600 * 24 * 8).toISOString(),
      isPublic: true,
      tileText: 'Many of us are rehearsing replies we do not yet have the breath to send. The rehearsal itself becomes the debt we feel we owe.',
      motif: 'fog',
      signature: 'affection,highAnt,medMem,sticky,thinCap',
      resonanceCount: 47,
      contributedSignals: true
    },
    {
      id: 'tile_ex2',
      mapId: 'map_ex2',
      createdAt: new Date(Date.now() - 1000 * 3600 * 24 * 21).toISOString(),
      publishedAt: new Date(Date.now() - 1000 * 3600 * 24 * 20).toISOString(),
      isPublic: true,
      tileText: 'The hardest handoff is often the one that would require us to be both accountable and small at the same time. Silence sometimes protects the possibility of both.',
      motif: 'knot',
      signature: 'repair,medAnt,highMem,knot,thinCap',
      resonanceCount: 31,
      contributedSignals: true
    },
    {
      id: 'tile_ex3',
      mapId: 'map_ex3',
      createdAt: new Date(Date.now() - 1000 * 3600 * 24 * 3).toISOString(),
      publishedAt: new Date(Date.now() - 1000 * 3600 * 24 * 2).toISOString(),
      isPublic: true,
      tileText: 'A delayed reply is rarely a single message. It is a whole correspondence we are afraid of reopening or unable to carry forward cleanly.',
      motif: 'letters',
      signature: 'information,highAnt,highMem,sticky,modestCap',
      resonanceCount: 19,
      contributedSignals: false
    }
  ]
  for (const t of examples) tiles[t.id] = t
  saveTiles(tiles)
}

export function listPublicTilesLocal(): WisdomTile[] {
  seedExampleTilesIfEmpty()
  const all = loadTiles()
  return Object.values(all).filter(t => t.isPublic).sort((a, b) => (b.publishedAt || b.createdAt).localeCompare(a.publishedAt || a.createdAt))
}

export function getTile(id: string): WisdomTile | null {
  const all = loadTiles()
  return all[id] || null
}

export function saveLocalTile(tile: WisdomTile) {
  const all = loadTiles()
  all[tile.id] = tile
  saveTiles(all)
}

export function resonateLocal(tileId: string, fp: string): number {
  // In local mode we just bump; real worker does day-bucket + fp dedupe
  const all = loadTiles()
  const t = all[tileId]
  if (!t) return 0
  t.resonanceCount = (t.resonanceCount || 0) + 1
  saveTiles(all)
  return t.resonanceCount
}

// Helper to compute a compact signature string from signals (for filtering + "trace this")
export function computeSignature(s: Signals): string {
  const care = s.care_burdens[0] || 'mixed'
  const ant = ['low', 'med', 'high'][s.anticipation.level - 1]
  const mem = ['low', 'med', 'high'][s.memory.level - 1]
  const hand = s.handoff.level === 3 ? 'knot' : s.handoff.level === 2 ? 'sticky' : 'open'
  const cap = s.capacity.level === 1 ? 'thin' : s.capacity.level === 2 ? 'modest' : 'full'
  return `${care},${ant}Ant,${mem}Mem,${hand},${cap}Cap`
}
